import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

/**
 * Shows a garage customer's name and mobile with a pencil icon to edit both inline.
 * Saves to the garage customer record, so the change applies to all of their bookings.
 *
 * Props:
 * - customer: { id, garage_id?, customer_name, mobile }
 * - garageId: falls back to customer.garage_id
 * - editable: hide the pencil when false (or when the customer has no id)
 * - onSaved(updated): called with { customer_name, mobile } after a successful save
 * - onEditingChange(isEditing): optional, notified when edit mode opens/closes
 * - layout: 'stacked' (name above mobile) or 'inline' (side by side)
 * - nameTypographyProps / mobileTypographyProps: style overrides for the read-only view
 */
const CustomerContactEditor = ({
  customer,
  garageId,
  editable = true,
  onSaved,
  onEditingChange,
  layout = 'stacked',
  nameTypographyProps = {},
  mobileTypographyProps = {},
}) => {
  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [mobileInput, setMobileInput] = useState('');
  const [nameError, setNameError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [saving, setSaving] = useState(false);

  const resolvedGarageId = garageId || customer?.garage_id;
  const canEdit = editable && !!customer?.id && !!resolvedGarageId;

  const setEditMode = (value) => {
    setEditing(value);
    onEditingChange?.(value);
  };

  const handleStart = () => {
    setNameInput(customer?.customer_name || '');
    setMobileInput(customer?.mobile || '');
    setNameError('');
    setMobileError('');
    setEditMode(true);
  };

  const handleCancel = () => {
    setNameError('');
    setMobileError('');
    setEditMode(false);
  };

  const handleSave = async () => {
    const customer_name = nameInput.trim();
    const mobile = mobileInput.trim();

    let invalid = false;
    if (!customer_name || customer_name.length > 255) {
      setNameError('Name is required (max 255 characters)');
      invalid = true;
    }
    if (mobile.length < 10 || mobile.length > 20) {
      setMobileError('Mobile number must be between 10 and 20 characters');
      invalid = true;
    }
    if (invalid) return;

    const changes = {};
    if (customer_name !== customer.customer_name) changes.customer_name = customer_name;
    if (mobile !== customer.mobile) changes.mobile = mobile;
    if (Object.keys(changes).length === 0) {
      handleCancel();
      return;
    }

    setSaving(true);
    try {
      await apiService.updateGarageCustomer(resolvedGarageId, customer.id, changes);
      onSaved?.({ customer_name, mobile });
      setEditMode(false);
    } catch (error) {
      console.error('Error updating customer details:', error);
      const detail = error.response?.data?.detail ?? error.detail;
      setMobileError(typeof detail === 'string' ? detail : 'Failed to update customer details');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', flex: 1 }}>
        <TextField
          size="small"
          label="Name"
          value={nameInput}
          autoFocus
          onChange={(e) => {
            setNameInput(e.target.value);
            setNameError('');
          }}
          onKeyDown={handleKeyDown}
          error={!!nameError}
          helperText={nameError}
          inputProps={{ maxLength: 255 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <TextField
          size="small"
          label="Mobile"
          value={mobileInput}
          onChange={(e) => {
            setMobileInput(e.target.value);
            setMobileError('');
          }}
          onKeyDown={handleKeyDown}
          error={!!mobileError}
          helperText={mobileError}
          inputProps={{ maxLength: 20, inputMode: 'tel' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving || !nameInput.trim() || !mobileInput.trim()}
            sx={{ textTransform: 'none', height: 40 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
          </Button>
          <Button
            size="small"
            onClick={handleCancel}
            disabled={saving}
            sx={{ textTransform: 'none', height: 40 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  const inline = layout === 'inline';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: inline ? 'center' : 'flex-start',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: inline ? 'row' : 'column',
          alignItems: inline ? 'center' : 'flex-start',
          gap: inline ? 2 : 0.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          {...nameTypographyProps}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, ...nameTypographyProps.sx }}
        >
          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          {customer?.customer_name}
        </Typography>
        {customer?.mobile && (
          <Typography
            variant="body2"
            color="text.secondary"
            {...mobileTypographyProps}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, ...mobileTypographyProps.sx }}
          >
            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            {customer.mobile}
          </Typography>
        )}
      </Box>
      {canEdit && (
        <Tooltip title="Edit name and mobile">
          <IconButton
            size="small"
            onClick={handleStart}
            aria-label="Edit customer name and mobile"
            sx={{ color: 'primary.main', p: 0.5 }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default CustomerContactEditor;
